// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/landing.ts
// (Lovable reference implementation, backlog LAUNCH-3). Covers the public
// marketing landing page (`/`) and the dashboard's first-run onboarding
// banner — grouped together since both shipped from the same Lovable pass.
export const en = {
  "landing.meta.title": "SnapDraw — Quotes & AIA Pay Applications for the Trades",
  "landing.meta.desc":
    "SnapDraw helps contractors in the trades build professional quotes and AIA G702/G703 applications for payment — from their phone, in English or Spanish.",

  "landing.nav.login": "Log in",
  "landing.nav.trial": "Start free trial",

  "landing.hero.eyebrow": "For the trades.",
  "landing.hero.title.1": "Quotes and G702/G703",
  // Non-breaking space between "on" and "site." (Lovable feedback,
  // 2026-08-29): "site." was dropping onto its own orphan line at
  // laptop widths. A real Unicode NBSP character here (not the HTML
  // entity, which would render as literal text "&nbsp;" since this is
  // plain string content in JSX) is deterministic across every browser,
  // unlike `text-wrap: balance`.
  "landing.hero.title.2": "pay apps, built on\u00A0site.",
  "landing.hero.sub":
    "SnapDraw turns line items into professional quotes and AIA applications for payment — retainage, schedule of values and carry-forward math handled for you.",
  "landing.hero.cta": "Start free trial",
  "landing.hero.ctaNote": "14 days free · No credit card required",
  "landing.hero.secondary": "See pricing",

  // Store badge row (backlog LAUNCH-26) — only rendered once at least one
  // of APP_STORE_URL/GOOGLE_PLAY_URL (src/lib/config.ts) is actually set.
  "landing.mobileStores.label": "Take SnapDraw to the field.",
  "landing.mobileStores.appleAlt": "Download SnapDraw for Contractors on the App Store",
  "landing.mobileStores.googleAlt": "Get SnapDraw for Contractors on Google Play",

  "landing.stat.quote.value": "2 MIN",
  "landing.stat.quote.label": "Avg quote",
  "landing.stat.g702.value": "G702/703",
  "landing.stat.g702.label": "AIA billing",
  "landing.stat.lang.value": "EN / ES",
  "landing.stat.lang.label": "Bilingual",
  "landing.stat.seats.value": "3 USERS",
  "landing.stat.seats.label": "Included",

  "landing.features.eyebrow": "What SnapDraw does",
  "landing.features.title": "Built for the way subs actually bill",
  "landing.features.sub":
    "No generic invoicing templates. Every screen is shaped around construction paperwork.",

  "landing.feature.quotes.title": "Fast, professional quotes",
  "landing.feature.quotes.body":
    "Labor and material line items, markup and sales tax — priced, totaled and ready to send as a clean PDF in about two minutes.",
  "landing.feature.aia.title": "G702/G703 pay applications",
  "landing.feature.aia.body":
    "The monthly pay apps GCs require on subcontract work. Retainage, schedule of values and period-to-period carry-forward are computed for you — no spreadsheet gymnastics.",
  "landing.feature.catalog.title": "Clients & rate catalog",
  "landing.feature.catalog.body":
    "Save your clients and your rates once — journeyman hours, conduit, panels, assemblies — and drop them into every document without retyping.",
  "landing.feature.reports.title": "Know what you're owed",
  "landing.feature.reports.body":
    "An owed-first reports view: outstanding balances, aging buckets, and exactly who to call next.",
  "landing.feature.bilingual.title": "English & Spanish, throughout",
  "landing.feature.bilingual.body":
    "Every screen and PDF header works in English or Spanish — switch with one tap, on any device.",
  "landing.feature.mobile.title": "Web and mobile",
  "landing.feature.mobile.body":
    "Start in your browser or use the native SnapDraw for Contractors app on supported iPhone, iPad, and Android devices — with the same account and documents wherever you work.",
  "landing.feature.team.title": "Up to 3 user accounts",
  "landing.feature.team.body":
    "Your subscription covers three logins — bring in a partner, your foreman or whoever does the billing, at no extra cost per seat.",
  "landing.feature.photos.title": "Photos that print on the quote",
  "landing.feature.photos.body":
    "Attach up to 8 job-site photos — existing conditions, before shots — straight from your phone. They print on the PDF your client sees, with captions.",

  "landing.pricing.eyebrow": "Pricing",
  "landing.pricing.title": "One plan. That's the pitch.",
  "landing.pricing.sub": "Everything included, no tiers, no seat math.",
  "landing.pricing.plan": "SnapDraw — Full Access",
  "landing.pricing.price": "$9.99",
  "landing.pricing.perMonth": "/ month",
  "landing.pricing.introBadge": "First month 50% off",
  "landing.pricing.intro": "$5",
  "landing.pricing.introNote": "for your first month, then $9.99/mo",
  "landing.pricing.trial": "14-day free trial · No credit card required",
  "landing.pricing.cta": "Start free trial",
  "landing.pricing.cancel": "Cancel anytime.",
  "landing.pricing.included.1": "Unlimited quotes and invoices",
  "landing.pricing.included.2": "G702 / G703 pay applications",
  "landing.pricing.included.3": "Client directory and pricing catalog",
  "landing.pricing.included.4": "PDF export, English & Spanish",
  "landing.pricing.included.5": "Up to 3 user accounts",

  "landing.footer.tagline": "Quotes and AIA pay applications for the trades.",
  "landing.footer.domain": "snapdraw.app",
} as const;

export const es: Record<keyof typeof en, string> = {
  "landing.meta.title": "SnapDraw — Cotizaciones y solicitudes de pago AIA para los oficios",
  "landing.meta.desc":
    "SnapDraw ayuda a contratistas de los oficios a crear cotizaciones profesionales y solicitudes de pago AIA G702/G703 — desde el teléfono, en inglés o español.",

  "landing.nav.login": "Iniciar sesión",
  "landing.nav.trial": "Prueba gratis",

  "landing.hero.eyebrow": "Para los oficios.",
  "landing.hero.title.1": "Cotizaciones y solicitudes",
  "landing.hero.title.2": "G702/G703, hechas en\u00A0obra.",
  "landing.hero.sub":
    "SnapDraw convierte partidas en cotizaciones profesionales y solicitudes de pago AIA — retención, tabla de valores y cálculos de arrastre incluidos.",
  "landing.hero.cta": "Prueba gratis",
  "landing.hero.ctaNote": "14 días gratis · Sin tarjeta de crédito",
  "landing.hero.secondary": "Ver precios",

  "landing.mobileStores.label": "Lleva SnapDraw contigo a la obra.",
  "landing.mobileStores.appleAlt": "Descarga SnapDraw for Contractors en el App Store",
  "landing.mobileStores.googleAlt": "Obtén SnapDraw for Contractors en Google Play",

  "landing.stat.quote.value": "2 MIN",
  "landing.stat.quote.label": "Cotización prom.",
  "landing.stat.g702.value": "G702/703",
  "landing.stat.g702.label": "Facturación AIA",
  "landing.stat.lang.value": "EN / ES",
  "landing.stat.lang.label": "Bilingüe",
  "landing.stat.seats.value": "3 USUARIOS",
  "landing.stat.seats.label": "Incluidos",

  "landing.features.eyebrow": "Lo que hace SnapDraw",
  "landing.features.title": "Hecho para la forma en que los subs facturan",
  "landing.features.sub":
    "Nada de plantillas genéricas. Cada pantalla está diseñada para el papeleo de la construcción.",

  "landing.feature.quotes.title": "Cotizaciones rápidas y profesionales",
  "landing.feature.quotes.body":
    "Partidas de mano de obra y material, margen e impuestos — calculadas, totalizadas y listas para enviar como PDF en unos dos minutos.",
  "landing.feature.aia.title": "Solicitudes de pago G702/G703",
  "landing.feature.aia.body":
    "Las solicitudes mensuales que el GC exige en el trabajo de subcontrato. Retención, tabla de valores y arrastre de período a período calculados por ti — sin hojas de cálculo.",
  "landing.feature.catalog.title": "Clientes y catálogo de tarifas",
  "landing.feature.catalog.body":
    "Guarda tus clientes y tus tarifas una vez — horas de oficial, conduit, paneles, ensambles — y reúsalos en cada documento sin volver a escribir.",
  "landing.feature.reports.title": "Sabe cuánto te deben",
  "landing.feature.reports.body":
    "Reportes enfocados en lo que te deben: saldos pendientes, antigüedad de cuentas y a quién llamar primero.",
  "landing.feature.bilingual.title": "Inglés y español, en todo",
  "landing.feature.bilingual.body":
    "Cada pantalla y encabezado de PDF funciona en inglés o español — cambia con un toque, en cualquier dispositivo.",
  "landing.feature.mobile.title": "En web y móvil",
  "landing.feature.mobile.body":
    "Empieza en tu navegador o usa la app nativa SnapDraw for Contractors en dispositivos iPhone, iPad y Android compatibles — con la misma cuenta y los mismos documentos dondequiera que trabajes.",
  "landing.feature.team.title": "Hasta 3 cuentas de usuario",
  "landing.feature.team.body":
    "Tu suscripción incluye tres accesos — suma a tu socio, tu capataz o a quien haga la facturación, sin costo extra por usuario.",
  "landing.feature.photos.title": "Fotos que se imprimen en la cotización",
  "landing.feature.photos.body":
    "Adjunta hasta 8 fotos de obra — condiciones existentes, fotos de antes — directo desde tu teléfono. Se imprimen en el PDF que ve tu cliente, con leyendas.",

  "landing.pricing.eyebrow": "Precios",
  "landing.pricing.title": "Un solo plan. Esa es la propuesta.",
  "landing.pricing.sub": "Todo incluido, sin niveles, sin cargos por usuario.",
  "landing.pricing.plan": "SnapDraw — Acceso completo",
  "landing.pricing.price": "$9.99",
  "landing.pricing.perMonth": "/ mes",
  "landing.pricing.introBadge": "50% de descuento el primer mes",
  "landing.pricing.intro": "$5",
  "landing.pricing.introNote": "tu primer mes, luego $9.99/mes",
  "landing.pricing.trial": "Prueba de 14 días gratis · Sin tarjeta de crédito",
  "landing.pricing.cta": "Prueba gratis",
  "landing.pricing.cancel": "Cancela cuando quieras.",
  "landing.pricing.included.1": "Cotizaciones y facturas ilimitadas",
  "landing.pricing.included.2": "Solicitudes de pago G702 / G703",
  "landing.pricing.included.3": "Directorio de clientes y catálogo de precios",
  "landing.pricing.included.4": "Exportar PDF, en inglés y español",
  "landing.pricing.included.5": "Hasta 3 cuentas de usuario",

  "landing.footer.tagline": "Cotizaciones y solicitudes de pago AIA para los oficios.",
  "landing.footer.domain": "snapdraw.app",
};
